// src/ResetPassword.jsx
// Custom reset password UI using oobCode from Firebase email link.
// User enters password twice, all in your Loop UI.

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { firebaseAuth } from "./firebase";

function useQuery() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const query = useQuery();

  const oobCode = query.get("oobCode") || "";
  const mode = query.get("mode") || ""; // should be resetPassword

  const [email, setEmail] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");

  const [checking, setChecking] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    (async () => {
      setError("");
      setMsg("");

      try {
        if (mode && mode !== "resetPassword") {
          throw new Error("Link inválido (modo incorrecto).");
        }
        if (!oobCode) {
          throw new Error("Link inválido o incompleto. Vuelve a solicitar el restablecimiento.");
        }

        const mail = await verifyPasswordResetCode(firebaseAuth, oobCode);
        if (!mounted) return;
        setEmail(mail || "");
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "No se pudo verificar el enlace.");
      } finally {
        if (mounted) setChecking(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [oobCode, mode]);

  const onSubmit = async (e) => {
    e?.preventDefault();
    setError("");
    setMsg("");

    if (!pw1 || pw1.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (pw1 !== pw2) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setSaving(true);
    try {
      await confirmPasswordReset(firebaseAuth, oobCode, pw1);
      setMsg("Contraseña actualizada. Ya puedes iniciar sesión.");
      setTimeout(() => navigate("/login", { replace: true }), 900);
    } catch (err) {
      setError((err?.code ? `${err.code}: ` : "") + (err?.message || "No se pudo actualizar la contraseña."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-start justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-md border overflow-hidden mt-8">
        <div className="p-6">
          <h2 className="text-2xl font-semibold text-slate-800">Restablecer contraseña</h2>
          <p className="text-sm text-slate-500 mt-1">
            {checking ? "Verificando enlace..." : email ? `Cuenta: ${email}` : " "}
          </p>

          {error ? (
            <div
              className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              style={{ whiteSpace: "pre-wrap" }}
            >
              {error}
            </div>
          ) : null}

          {msg ? (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {msg}
            </div>
          ) : null}

          <form onSubmit={onSubmit} className="space-y-3 mt-5">
            <div>
              <label className="block text-sm text-slate-600 mb-1">Nueva contraseña</label>
              <input
                type="password"
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="w-full px-3 py-2 border rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                disabled={checking || !!error}
                required
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-600 mb-1">Repite la contraseña</label>
              <input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                placeholder="Repite la contraseña"
                className="w-full px-3 py-2 border rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                disabled={checking || !!error}
                required
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              disabled={checking || saving || !!error}
              className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg font-semibold"
            >
              {saving ? "Guardando..." : "Guardar contraseña"}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                className="text-sm text-slate-500 underline hover:text-slate-700"
              >
                Volver a login
              </button>
            </div>
          </form>

          <div className="mt-4 text-xs text-slate-400">
            Consejo: usa una contraseña segura y no la compartas.
          </div>
        </div>
      </div>
    </div>
  );
}